// src/components/CrashCourse/FinalExam/progressStore.js
//
// Fixes the "second time it makes you redo the modules" bug: CourseHub.jsx
// was reading `module.status` straight off the static `COURSE_DATA` import,
// which never changes — nothing ever wrote "completed" back into it. This
// module tracks real per-student, per-state progress in localStorage so
// revisiting a state (or reloading the page) remembers what's done.
//
// Swap the two localStorage calls at the bottom for real API calls
// (GET/POST /api/courses/progress/) when you wire this to Django — every
// function above that point stays the same.

const STORAGE_KEY = "ppca_course_progress_v1";

function currentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.email || "anonymous";
  } catch {
    return "anonymous";
  }
}

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeAll(all) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}
function key(stateId) {
  return `${currentUserId()}::${stateId}`;
}

const EMPTY = { completedModuleIds: [], certified: false, credentialId: null, issueDate: null, attempts: [] };

export function getProgress(stateId) {
  const all = readAll();
  return all[key(stateId)] || { ...EMPTY };
}

function saveProgress(stateId, progress) {
  const all = readAll();
  all[key(stateId)] = progress;
  writeAll(all);
  return progress;
}

export function markModuleComplete(stateId, moduleId) {
  const p = getProgress(stateId);
  if (!p.completedModuleIds.includes(moduleId)) {
    p.completedModuleIds = [...p.completedModuleIds, moduleId];
  }
  return saveProgress(stateId, p);
}

// status for a single module given its position in the course's module list
export function getModuleStatus(stateId, moduleId, index, allModuleIds) {
  const p = getProgress(stateId);
  if (p.completedModuleIds.includes(moduleId)) return "completed";
  if (index === 0) return "unlocked";
  const prevId = allModuleIds[index - 1];
  return p.completedModuleIds.includes(prevId) ? "unlocked" : "locked";
}

export function allModulesComplete(stateId, allModuleIds) {
  const p = getProgress(stateId);
  return allModuleIds.every((id) => p.completedModuleIds.includes(id));
}

export function completedCount(stateId, allModuleIds) {
  const p = getProgress(stateId);
  return allModuleIds.filter((id) => p.completedModuleIds.includes(id)).length;
}

export function recordAttempt(stateId, attempt) {
  const p = getProgress(stateId);
  p.attempts = [...p.attempts, { score: attempt.score, total: attempt.total, pct: attempt.pct, pass: attempt.pass, date: attempt.date }];
  return saveProgress(stateId, p);
}

export function setCertified(stateId, credentialId, issueDate) {
  const p = getProgress(stateId);
  p.certified = true;
  p.credentialId = credentialId;
  p.issueDate = issueDate;
  return saveProgress(stateId, p);
}

export function isCertified(stateId) {
  return getProgress(stateId).certified;
}

// Dev/QA helper — not used by any component, handy in the browser console
// when testing the flow repeatedly: window.__resetPPCAProgress("WA")
if (typeof window !== "undefined") {
  window.__resetPPCAProgress = (stateId) => {
    const all = readAll();
    delete all[key(stateId)];
    writeAll(all);
    console.log(`Progress reset for ${stateId}. Reload the page.`);
  };
}
